import { useState, useEffect } from "react";
import { Form, Schema } from "rsuite";
import { StringType } from "schema-typed";
import { useHistory } from "react-router-dom";
import { Button } from "../../components/button.component";
import { TextInput } from "../../components/textInput.component";
import { ApiDataExchangeConfig } from "../../core/apis/dataExchangeConfig";
import { AppRouteUi} from "../../config/appRoute";
import { showSuccessToast, showErrorToast } from "../../components/toast.component";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { ActionsHHAConfigTable } from "../../core/redux/slices/sliceHHAConfigTable";
import { DoesObjectHasAnyInvalidValue } from "../../utils/formUtils";
import { SelectCompany } from "../data-exchange/selectCompany.component";
import { FaExclamationTriangle } from "react-icons/fa";

interface FormData {
  companyId: string;
  appName: string;
  appKey: string;
  appSecret: string;
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
  dataUrl: string;
  patientDataUrl: string;
  scope: string;
  grantType: string;
}

const formModel = Schema.Model({
  companyId: StringType()
    .isRequired('Company is required'),
  appName: StringType()
    .isRequired('App Name is required')
    .maxLength(200, 'App Name cannot exceed 200 characters'),
  appKey: StringType()
    .isRequired('App Key is required')
    .maxLength(200, 'App Key cannot exceed 200 characters'),
  appSecret: StringType()
    .isRequired('App Secret is required')
    .maxLength(200, 'App Secret cannot exceed 200 characters'),
  clientId: StringType()
    .isRequired('Client ID is required')
    .maxLength(200, 'Client ID cannot exceed 200 characters'),
  clientSecret: StringType()
    .isRequired('Client Secret is required')
    .maxLength(200, 'Client Secret cannot exceed 200 characters'),
  tokenUrl: StringType()
    .isRequired('Token URL is required')
    .pattern(/^https?:\/\/.+/, 'Token URL must be a valid URL starting with http:// or https://'),
  dataUrl: StringType()
    .isRequired('Data URL is required')
    .pattern(/^https?:\/\/.+/, 'Data URL must be a valid URL starting with http:// or https://'),
  patientDataUrl: StringType()
    .isRequired('Patient Data URL is required')
    .pattern(/^https?:\/\/.+/, 'Patient Data URL must be a valid URL starting with http:// or https://'),
  scope: StringType()
    .isRequired('Scope is required'),
  grantType: StringType()
    .isRequired('Grant Type is required')
});

export const HHAExchangeConfigAdd = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [formError, setFormError] = useState<any>({});
  const [configExists, setConfigExists] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [formData, setFormData] = useState<FormData>({
    companyId: "",
    appName: "",
    appKey: "",
    appSecret: "",
    clientId: "",
    clientSecret: "",
    tokenUrl: "",
    dataUrl: "",
    patientDataUrl: "",
    scope: "",
    grantType: "",
  });

  // Check for existing config when company is selected
  const checkExistingConfig = async (companyId: string) => {
    try {
      const response = await ApiDataExchangeConfig.GetByCompanyId(companyId);
      if (response.isSuccess && response.data) {
        setConfigExists(true);
      } else {
        setConfigExists(false);
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to check existing configuration";
      showErrorToast({ isSuccess: false, message: errorMessage });
    }
  };

  // Validate form data
  useEffect(() => {
    const isEmpty = Object.values({ ...formData }).some(DoesObjectHasAnyInvalidValue);
    const errorCount = Object.keys(formError).length;
    setIsDisabled(isEmpty || !!errorCount);
  }, [formData, formError]);

  const handleSubmit = async () => {
    if (isDisabled) return;
    
    setIsLoading(true);
    try {
      const response = await ApiDataExchangeConfig.Create(formData);
      if (response.isSuccess) {
        showSuccessToast("HHA Configuration created successfully");
        dispatch(ActionsHHAConfigTable.setRefetch(true));
        history.push(AppRouteUi.HHAConfig.Root());
      } else {
        showErrorToast({ isSuccess: false, message: response.message || "Failed to create HHA configuration" });
      }
    } catch (error: any) {
      const errorMessage = error.message || "Failed to create HHA configuration";
      showErrorToast({ isSuccess: false, message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (formValue: any) => {
    setFormData(formValue);
  };

  const ExistingConfigBanner = () => (
    <div className="alert alert-warning d-flex align-items-center gap-3 p-4">
      <FaExclamationTriangle size={24} />
      <div>
        <h4 className="alert-heading mb-2">Configuration Already Exists</h4>
        <p className="mb-2">
          An HHA configuration already exists for {selectedCompanyName}.
        </p>
        <hr />
        <div className="mt-3">
          <Button
            className="btn-primary me-2"
            name="View Existing Config"
            onClick={() => history.push(AppRouteUi.HHAConfig.Root())}
          />
          <Button
            className="btn-outline-secondary"
            name="Select Different Company"
            onClick={() => {
              setConfigExists(false);
              setSelectedCompanyName("");
              setFormData(prev => ({ ...prev, companyId: "" }));
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="col-md-10 col-12 border-end px-4 pb-3 add-page-ctl">
      {configExists ? (
        <ExistingConfigBanner />
      ) : (
        <Form 
          fluid 
          onChange={handleChange} 
          formValue={formData} 
          onSubmit={handleSubmit}
          model={formModel}
          onCheck={setFormError}
          className="form-validation"
        >
          <div className="row">
            <div className="rs-form-group">
              <label className="rs-form-control-label">Company</label>
              <SelectCompany
                setSelectedCompany={(value, extra) => {
                  if (typeof value === 'string') {
                    setFormData(prev => ({ ...prev, companyId: value }));
                    if (extra?.companyName) {
                      setSelectedCompanyName(extra.companyName);
                    }
                    checkExistingConfig(value);
                  }
                }}
              />
            </div>
          </div>

          <div className="row">
            <TextInput name="appName" required>
              App Name
            </TextInput>
            <TextInput name="appKey" required>
              App Key
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="appSecret" required>
              App Secret
            </TextInput>
            <TextInput name="clientId" required>
              Client ID
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="clientSecret" required>
              Client Secret
            </TextInput>
            <TextInput name="tokenUrl" required>
              Token URL
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="dataUrl" required>
              Data URL
            </TextInput>
            <TextInput name="patientDataUrl" required>
              Patient Data URL
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="scope" required>
              Scope
            </TextInput>
            <TextInput name="grantType" required>
              Grant Type
            </TextInput>
          </div>

          <div className="d-flex justify-content-center mt-4">
            <Button
              className="px-5 me-3"
              name="Cancel"
              onClick={() => history.push(AppRouteUi.HHAConfig.Root())}
            />
            <Button
              className="px-5 btn-green"
              type="submit"
              loading={isLoading}
              disabled={isDisabled}
              name="Create"
            />
          </div>
        </Form>
      )}
    </div>
  );
};